import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Label,
    ModalDialog,
    SelectFilled,
    type OptionProps,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { FormEvent } from 'react';

import { useAmelioTranslations } from '@/hooks';

type MoveQuestionModalProps = {
    /** The editor mounts this dialog only while a question is being moved, so it opens by default. */
    isOpen?: boolean;
    questionLabel: string;
    competencyOptions: OptionProps[];
    isSubmitting: boolean;
    onConfirm: (targetCompetencyId: string) => void;
    onCancel: () => void;
};

export const MoveQuestionModal = ({
    isOpen = true,
    questionLabel,
    competencyOptions,
    isSubmitting,
    onConfirm,
    onCancel,
}: MoveQuestionModalProps) => {
    const { t } = useAmelioTranslations();

    const [targetCompetencyId, setTargetCompetencyId] = useState<string>('');

    const selectOptions = useMemo(
        () =>
            competencyOptions.map((option) => ({
                ...option,
                value: String(option.value ?? option.id),
            })),
        [competencyOptions],
    );

    const canSubmit = Boolean(targetCompetencyId) && !isSubmitting;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (canSubmit) {
            onConfirm(targetCompetencyId);
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onCancel}
            closeAriaLabel={t('common.close')}
            title={t('competencies.questions.move.title', { name: questionLabel })}
            size={'md'}
            onSubmit={handleSubmit}
            backdropBlur
            dataTestId={'question-move-modal'}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        size={'sm'}
                        onClick={onCancel}
                        dataTestId={'question-move-cancel'}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        size={'sm'}
                        disabled={!canSubmit}
                        dataTestId={'question-move-confirm'}
                    >
                        {t('competencies.questions.move.confirm')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-2'}>
                <Label required>{t('competencies.questions.move.targetLabel')}</Label>

                <SelectFilled
                    value={targetCompetencyId}
                    options={selectOptions}
                    onChange={(value) => setTargetCompetencyId(value)}
                    placeholder={t('competencies.questions.move.targetPlaceholder')}
                    emptyText={t('common.noResults')}
                    ariaLabel={t('competencies.questions.move.targetLabel')}
                    sort={'none'}
                    dataTestId={'question-move-target-select'}
                />
            </div>
        </ModalDialog>
    );
};
